import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import type {
  TCreateGuaranteesSupervisorSegmentRequest,
  TDeleteGuaranteesSupervisorSegmentSupervisorRequest,
  TFetchGuaranteesSupervisorSegmentsByGroupRequest,
  TGuaranteesSupervisorSegment,
  TGuaranteesThunkError,
} from '../types.ts';
import {
  getGuaranteesSupervisorSegmentsApiGroup,
  normalizeGuaranteesSupervisorSegments,
} from '../utils.ts';

export const fetchGuaranteesSupervisorSegments = createAsyncThunk<
  TGuaranteesSupervisorSegment[],
  void,
  { rejectValue: TGuaranteesThunkError }
>('guaranteesPage/fetchSupervisorSegments', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/guarantee-api/covenants/supervisors/segments');

    return Array.isArray(data) ? data : [];
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchGuaranteesSupervisorSegmentsByGroup = createAsyncThunk<
  TGuaranteesSupervisorSegment[],
  TFetchGuaranteesSupervisorSegmentsByGroupRequest,
  { rejectValue: TGuaranteesThunkError }
>(
  'guaranteesPage/fetchSupervisorSegmentsByGroup',
  async ({ groupKey, email }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/guarantee-api/suppervisors/${getGuaranteesSupervisorSegmentsApiGroup(groupKey)}/segments`,
        {
          params: email ? { email } : undefined,
        }
      );

      return normalizeGuaranteesSupervisorSegments(data);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const createGuaranteesSupervisorSegmentSupervisor = createAsyncThunk<
  void,
  TCreateGuaranteesSupervisorSegmentRequest,
  { rejectValue: TGuaranteesThunkError }
>(
  'guaranteesPage/createSupervisorSegmentSupervisor',
  async ({ segmentId, supervisorEmail, groupKey, email }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/guarantee-api/suppervisors/${segmentId}/segments`, {
        supervisorEmail,
      });

      if (groupKey) {
        await dispatch(fetchGuaranteesSupervisorSegmentsByGroup({ groupKey, email })).unwrap();
      }

      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteGuaranteesSupervisorSegmentSupervisor = createAsyncThunk<
  void,
  TDeleteGuaranteesSupervisorSegmentSupervisorRequest,
  { rejectValue: TGuaranteesThunkError }
>(
  'guaranteesPage/deleteSupervisorSegmentSupervisor',
  async ({ segmentId, supervisorId, groupKey, email }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/guarantee-api/suppervisors/${supervisorId}/segments/${segmentId}`);

      if (groupKey) {
        await dispatch(fetchGuaranteesSupervisorSegmentsByGroup({ groupKey, email })).unwrap();
      }

      return;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
